import { ColDef, IDateFilterParams, ValueGetterParams } from 'ag-grid-enterprise'
import { TFunction } from 'react-i18next'
import { parseDate } from '../../../config/dayjsConfig'
import { formatValueByType } from '../../../utils/helpers'
import { Match } from '../types'
import {
  getAcceptanceStatus,
  getAmountA,
  getAmountB,
  getClassifications,
  getCompanyName,
  getCounterpartyName,
  getDateA,
  getDateB,
  getCurrencyLabel,
  getDifference,
  getLedgerAmountA,
  getLedgerAmountB,
  getLedgerCurrencyA,
  getLedgerCurrencyB,
  getPurchaseInvoiceNumber,
  getReference,
  getSalesInvoiceNumber,
  getSidePaymentStatus
} from '../utils/reconciliationHelpers'
import {
  acceptanceStatusLabel,
  AcceptanceStatusCellRenderer,
  ActionsCellRenderer,
  ClassificationCellRenderer,
  ClassificationDotCellRenderer,
  CurrencyCellRenderer,
  DifferenceCellRenderer,
  LedgerAmountCellRenderer,
  ReconciliationGridContext,
  SidePaymentStatusCellRenderer,
  sideStatusLabel
} from './reconciliationCellRenderers'

const currencyFormatter = (params: { value?: number }) =>
  formatValueByType(params.value, undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })

// valueGetter wrapper: run the helper only once row data is present, else undefined.
const fromData =
  <R>(select: (match: Match) => R) =>
  (params: { data?: Match }): R | undefined =>
    params.data ? select(params.data) : undefined

const companyNameGetter = (params: ValueGetterParams<Match, string, ReconciliationGridContext>) =>
  params.data && params.context ? getCompanyName(params.data, params.context.companyNameById) : undefined

const counterpartyNameGetter = (params: ValueGetterParams<Match, string, ReconciliationGridContext>) =>
  params.data && params.context ? getCounterpartyName(params.data, params.context.companyNameById) : undefined

const dateFilterParams: IDateFilterParams = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    const cellDate = parseDate(cellValue as string | undefined)?.toDate()
    if (!cellDate) return 0
    if (cellDate < filterLocalDateAtMidnight) return -1
    if (cellDate > filterLocalDateAtMidnight) return 1
    return 0
  }
}

const missingVoucherCellStyle = { backgroundColor: '#fdf3e0', color: '#7a5510' }

// Set-filter list labels come from the same label fn the cell renderer uses.
const statusFilterParams = <V>(label: (value: V, t: TFunction) => string, t: TFunction) => ({
  valueFormatter: (params: { value: V }) => label(params.value, t)
})

export const getReconciliationColumnDefs = (t: TFunction, groupCurrency: string): ColDef<Match>[] => [
  {
    headerName: '',
    colId: 'classificationDot',
    width: 12,
    sortable: false,
    resizable: false,
    filter: false,
    floatingFilter: false,
    suppressColumnsToolPanel: true,
    lockVisible: true,
    cellRenderer: ClassificationDotCellRenderer
  },
  {
    headerName: `${t('icReconciliation:tableCompany', 'Yritys')} (A)`,
    colId: 'companyA',
    valueGetter: companyNameGetter,
    width: 150,
    cellRenderer: 'agGroupCellRenderer',
    filter: 'agSetColumnFilter',
    cellStyle: params => (params.data && !params.data.a ? missingVoucherCellStyle : undefined)
  },
  {
    headerName: `${t('icReconciliation:tableCounterparty', 'Vastapuoli')} (B)`,
    colId: 'counterpartyB',
    valueGetter: counterpartyNameGetter,
    width: 150,
    valueFormatter: params => (params.value ? `→ ${params.value}` : ''),
    filter: 'agSetColumnFilter',
    cellStyle: params => (params.data && !params.data.b ? missingVoucherCellStyle : undefined)
  },
  {
    headerName: t('icReconciliation:tableSalesInvoice', 'Myyntilasku A'),
    colId: 'salesInvoiceA',
    valueGetter: fromData(getSalesInvoiceNumber),
    width: 130,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: t('icReconciliation:tablePurchaseInvoice', 'Ostolasku B'),
    colId: 'purchaseInvoiceB',
    valueGetter: fromData(getPurchaseInvoiceNumber),
    width: 130,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: t('icReconciliation:tableReference', 'Viite'),
    colId: 'reference',
    valueGetter: fromData(getReference),
    // No `flex` — a flex column breaks grid-state restore (see useGridStatePersistence).
    width: 200,
    minWidth: 140,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: t('icReconciliation:tableBookingDateA', 'Kirjauspäivä A'),
    colId: 'date',
    valueGetter: fromData(getDateA),
    width: 120,
    type: 'datePicker',
    filterParams: dateFilterParams
  },
  {
    headerName: t('icReconciliation:tableBookingDateB', 'Kirjauspäivä B'),
    colId: 'bookingDateB',
    valueGetter: fromData(getDateB),
    width: 120,
    type: 'datePicker',
    filterParams: dateFilterParams
  },
  {
    headerName: t('icReconciliation:tableClassification', 'Luokitus'),
    colId: 'classification',
    valueGetter: fromData(getClassifications),
    width: 120,
    sortable: false,
    cellRenderer: ClassificationCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: (params: { value: string }) => t(`icReconciliation:classification-${params.value}`, params.value)
    }
  },
  {
    headerName: t('icReconciliation:tableAcceptance', 'Käsittely'),
    colId: 'acceptance',
    hide: true,
    valueGetter: fromData(getAcceptanceStatus),
    width: 150,
    sortable: false,
    cellRenderer: AcceptanceStatusCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: statusFilterParams(acceptanceStatusLabel, t)
  },
  {
    headerName: t('icReconciliation:tableCurrency', 'Valuutta'),
    colId: 'currency',
    valueGetter: params => (params.data ? getCurrencyLabel(params.data, groupCurrency) : undefined),
    width: 110,
    sortable: false,
    cellRenderer: CurrencyCellRenderer,
    filter: 'agSetColumnFilter'
  },
  {
    headerName: `${t('icReconciliation:tableAmountA', 'Summa A')} (${groupCurrency})`,
    colId: 'amountA',
    valueGetter: fromData(getAmountA),
    width: 140,
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
    cellRenderer: LedgerAmountCellRenderer(getLedgerAmountA, getLedgerCurrencyA),
    filter: 'agNumberColumnFilter'
  },
  {
    headerName: `${t('icReconciliation:tableAmountB', 'Summa B')} (${groupCurrency})`,
    colId: 'amountB',
    valueGetter: fromData(getAmountB),
    width: 140,
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
    cellRenderer: LedgerAmountCellRenderer(getLedgerAmountB, getLedgerCurrencyB),
    filter: 'agNumberColumnFilter'
  },
  {
    headerName: `${t('icReconciliation:tableDifference', 'Erotus')} (${groupCurrency})`,
    colId: 'difference',
    width: 140,
    type: 'numericColumn',
    valueGetter: fromData(getDifference),
    valueFormatter: currencyFormatter,
    cellRenderer: DifferenceCellRenderer,
    filter: 'agNumberColumnFilter'
  },
  {
    headerName: t('icReconciliation:tableStatusA', 'Tila A'),
    colId: 'statusA',
    valueGetter: fromData(match => getSidePaymentStatus(match.a)),
    width: 120,
    sortable: false,
    cellRenderer: SidePaymentStatusCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: statusFilterParams(sideStatusLabel, t)
  },
  {
    headerName: t('icReconciliation:tableStatusB', 'Tila B'),
    colId: 'statusB',
    valueGetter: fromData(match => getSidePaymentStatus(match.b)),
    width: 120,
    sortable: false,
    cellRenderer: SidePaymentStatusCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: statusFilterParams(sideStatusLabel, t)
  },

  {
    headerName: t('icReconciliation:tableActions', 'Toiminnot'),
    colId: 'actions',
    pinned: 'right',
    width: 70,
    sortable: false,
    resizable: false,
    filter: false,
    floatingFilter: false,
    suppressColumnsToolPanel: true,
    lockVisible: true,
    cellRenderer: ActionsCellRenderer
  }
]
